/**
 * The pure half of ENHANCED EFFECTS: a fixed-capacity ring of particles that
 * spawn, drift and die. No three.js, so the whole lifecycle is unit-testable
 * without a GPU — the same split `smoke-trail.ts` and `effect-sprites.ts` make
 * against `sprite-effects.ts`.
 *
 * **A ring, not a scan.** `smoke-trail.ts` picks a slot by walking all 32 puffs
 * for the oldest. That is fine at 32 and wrong at 320: this pool spawns up to
 * ~130 particles a second and a linear oldest-search per spawn is a quadratic
 * that shows up on exactly the frame the fight gets interesting. A cursor
 * walking the array in order finds a free slot in O(1) amortised and, because
 * every particle in a system shares one lifetime band, lands on the oldest one
 * anyway when the pool is saturated.
 *
 * **Nothing here allocates.** The particles are built once by
 * `createParticleSystem` and mutated in place for the life of the scene, which
 * is the rule `advanceSmoke` already states and the reason the perf lane can
 * profile a burning fight without finding this in the GC log.
 *
 * **Determinism.** These are presentation, not physics: nothing here is read
 * back into the sim, into a hash, or onto the duel wire. The jitter is still
 * drawn from a seeded LCG rather than `Math.random` so a test can assert a
 * spawn's exact scatter, and so a `.VCR` replay of the same fight scatters its
 * sparks the same way twice.
 */

/**
 * One live particle. Flat mutable fields rather than vectors: this is written
 * to a `Float32Array` instance buffer every frame and a `Vector3` per component
 * would be three allocations per particle at construction and a pointer chase
 * per field afterwards.
 */
export interface Particle {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Seconds lived; `>= life` means the slot is free. */
  age: number;
  /** Seconds this particle is alive for. Zero on a never-used slot. */
  life: number;
  /** Metres — the billboard's half-extent, grown by `growth`. */
  radius: number;
  /** Metres per second the radius grows by. */
  growth: number;
  /** Buoyancy in m/s²: positive rises (smoke, flame), negative falls (a spark). */
  rise: number;
  /** Linear velocity decay per second. Terminal rise is `rise / drag`. */
  drag: number;
}

function emptyParticle(): Particle {
  return { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, age: 0, life: 0, radius: 0, growth: 0, rise: 0, drag: 0 };
}

export interface ParticleSystem {
  readonly particles: Particle[];
  /** Next slot the ring will try. */
  cursor: number;
  /** Fractional particles owed, carried between frames so a rate is frame-rate independent. */
  emission: number;
  /** LCG state — see the header on why this is seeded rather than `Math.random`. */
  seed: number;
}

/** Numerical Recipes' 32-bit LCG constants; any full-period pair would do. */
const LCG_MULTIPLIER = 1_664_525;
const LCG_INCREMENT = 1_013_904_223;
const LCG_MODULUS = 0x1_0000_0000;

export function createParticleSystem(capacity: number, seed = 1): ParticleSystem {
  return { particles: Array.from({ length: capacity }, emptyParticle), cursor: 0, emission: 0, seed };
}

/** The next value in `[0, 1)`. Advances the system's own state — cosmetic only. */
export function nextUnit(system: ParticleSystem): number {
  system.seed = (system.seed * LCG_MULTIPLIER + LCG_INCREMENT) % LCG_MODULUS;
  return system.seed / LCG_MODULUS;
}

/** The next value in `[-spread, spread)`. */
export function nextSpread(system: ParticleSystem, spread: number): number {
  return (nextUnit(system) * 2 - 1) * spread;
}

/**
 * The slot the next spawn should use. Walks forward from the cursor for a dead
 * particle and, failing that, recycles whatever the cursor is on — see the
 * header for why that is the oldest one in a saturated pool.
 */
export function claimSlot(system: ParticleSystem): Particle {
  const { particles } = system;
  for (let step = 0; step < particles.length; step += 1) {
    const index = (system.cursor + step) % particles.length;
    const particle = particles[index];
    if (particle && particle.age >= particle.life) {
      system.cursor = (index + 1) % particles.length;
      return particle;
    }
  }
  const recycled = particles[system.cursor];
  system.cursor = (system.cursor + 1) % particles.length;
  // A system is never built with zero capacity, so this is unreachable; the
  // fallback exists because the index type cannot say so.
  return recycled ?? emptyParticle();
}

/** Every value a spawn sets, so a caller states the whole particle in one call. */
export interface ParticleSpawn {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly vx: number;
  readonly vy: number;
  readonly vz: number;
  readonly life: number;
  readonly radius: number;
  readonly growth: number;
  readonly rise: number;
  readonly drag: number;
}

export function spawnParticle(system: ParticleSystem, spawn: ParticleSpawn): Particle {
  const particle = claimSlot(system);
  particle.x = spawn.x;
  particle.y = spawn.y;
  particle.z = spawn.z;
  particle.vx = spawn.vx;
  particle.vy = spawn.vy;
  particle.vz = spawn.vz;
  particle.age = 0;
  particle.life = spawn.life;
  particle.radius = spawn.radius;
  particle.growth = spawn.growth;
  particle.rise = spawn.rise;
  particle.drag = spawn.drag;
  return particle;
}

/**
 * Ages every live particle by `dt`, applying buoyancy, drag and growth.
 *
 * Drag is linear (`v *= 1 - drag·dt`) rather than exponential: at the frame
 * rates this runs at the two are indistinguishable, and `Math.pow` per particle
 * per frame is a few hundred transcendentals a frame for a difference nobody
 * can see. Buoyancy is integrated before drag, so a particle settles at a
 * terminal `rise / drag` — which is how the smoke's rise rate is tied to the
 * sprite trail's own `PUFF_RISE_MPS` rather than picked twice.
 */
export function advanceParticles(system: ParticleSystem, dt: number): void {
  if (dt <= 0) return;
  for (const particle of system.particles) {
    if (particle.age >= particle.life) continue;
    particle.age += dt;
    particle.vy += particle.rise * dt;
    const keep = Math.max(0, 1 - particle.drag * dt);
    particle.vx *= keep;
    particle.vy *= keep;
    particle.vz *= keep;
    particle.x += particle.vx * dt;
    particle.y += particle.vy * dt;
    particle.z += particle.vz * dt;
    particle.radius += particle.growth * dt;
  }
}

/** 0 at birth, 1 at death — what both the colour ramp and the fade are functions of. */
export function lifeFraction(particle: Particle): number {
  if (particle.life <= 0) return 1;
  return Math.min(1, particle.age / particle.life);
}

export function particleIsLive(particle: Particle): boolean {
  return particle.age < particle.life;
}

export function liveParticleCount(system: ParticleSystem): number {
  let live = 0;
  for (const particle of system.particles) if (particleIsLive(particle)) live += 1;
  return live;
}

/**
 * A flame's opacity: full at birth, gone at death, straight down.
 *
 * A licking flame is brightest where it leaves the airframe and thins away at
 * the tip — it has no body to hold, which is the difference from a puff and the
 * reason these are two functions and not one with a parameter. Squared rather
 * than linear so the dying end of the ramp, `EXPLO.BMP`'s deep reds, is mostly
 * stippled out by the time it gets there: measured on a real GPU, a linear fade
 * left a red flare hanging off the fuselage where a flame should have tapered.
 */
export function fireOpacity(particle: Particle): number {
  const remaining = 1 - lifeFraction(particle);
  return remaining * remaining;
}

/**
 * A puff's opacity: holds most of its body through the first half of its life
 * and thins away over the second (`1 - t²`).
 *
 * That is the port's own softening, the same addition `smoke-trail.ts`'s
 * `puffOpacity` makes and for the same reason — the original deletes a puff
 * outright at 2.52 s, having no way to fade one. Both are cut through the
 * ordered-dither stipple, so neither ever asks the depth buffer for a favour.
 */
export function smokeOpacity(particle: Particle): number {
  const t = lifeFraction(particle);
  return 1 - t * t;
}

/**
 * Adds `rate · dt` to the system's owed count and returns how many whole
 * particles to spawn now, leaving the remainder for the next frame.
 *
 * Shared by every emitter in a system, exactly as `advanceSmoke` shares one
 * accumulator across its sources: the aggregate rate is right, and which source
 * gets the particle that tips the count over is not something anyone can see.
 *
 * `cap` is a per-call ceiling, and anything it refuses is **discarded** rather
 * than banked. A frame that hitches for a second would otherwise owe a hundred
 * particles and pay them all out on the next frame as one solid ball — the
 * backlog is not a debt worth honouring when the thing it buys is smoke.
 */
export function owedParticles(system: ParticleSystem, ratePerSecond: number, dt: number, cap: number): number {
  system.emission += ratePerSecond * dt;
  const whole = Math.min(cap, Math.floor(system.emission));
  system.emission = Math.min(system.emission - whole, 1);
  return whole;
}
